import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import {
  SiteTour,
  SiteTourService,
  SiteTourStep,
  UpdateTourWithStepsRequest,
} from '@app/shared/services/site-tours.service';
import { MTX_DRAWER_DATA, MtxDrawerRef } from '@ng-matero/extensions/drawer';
import { HelpersService } from '@app/shared/services/helpers.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';
import { finalize } from 'rxjs';
import { jsonValidator } from '@app/shared/validators/json.validator';
import { ErrorMessages } from '../../../../../../shared/constants';

@Component({
  standalone: false,
  selector: 'app-edit-site-tours',
  templateUrl: './edit-tours.component.html',
})
export class EditToursComponent implements OnInit {
  public isEditing: boolean;
  public loading: boolean = true;
  public isSubmitting = false;
  public form: FormGroup;
  public siteTour: SiteTour;
  public deletedStepIds: number[] = [];
  public settingsPlaceholder: string = `Optional Example: ${JSON.stringify({ theme: 'light', zIndex: 1300 })}`;
  public stepSettingsPlaceholder: string = `Optional Example: ${JSON.stringify({ position:"center", linkTarget: "_blank", finishButtonLabel:"Got it" })}`;

  constructor(
    public drawerRef: MtxDrawerRef<EditToursComponent>,
    @Inject(MTX_DRAWER_DATA) public data: { siteTour?: SiteTour },
    private formBuilder:  FormBuilder,
    private siteTourService: SiteTourService,
    private helpersService: HelpersService,
    private logger: LoggerService,
    private bannerNotificationsService: BannerNotificationsService
  ) {
  }

  ngOnInit() {
    this.loading = true;
    this.siteTour = this.data.siteTour;
    this.isEditing = !!this.siteTour;
    this.createForm();
  }

  public createForm(): void {
    this.form = this.formBuilder.group({
      name: ['', [Validators.required]],
      multipage: [0],
      pageSlug: ['', [Validators.required]],
      isActive: [1],
      settings: ['', [jsonValidator()]],
      steps: this.formBuilder.array([])
    });
    this.setupTour();
    this.loading = false;
  }

  // Steps FormArray getter
  get steps(): FormArray {
    return this.form.get('steps') as FormArray;
  }

  // Create a new step FormGroup
  public createStepFormGroup(step?: SiteTourStep): FormGroup {
    return this.formBuilder.group({
      id: [step?.id || null],
      title: [step?.title || '', [Validators.required]],
      content: [step?.content || '', [Validators.required]],
      pageSlug: [step?.pageSlug || ''],
      elementSelector: [step?.elementSelector || ''],
      linkUrl: [step?.linkUrl || ''],
      linkLabel: [step?.linkLabel || ''],
      settings: [(step?.settings ? JSON.stringify(step.settings) : ''), [jsonValidator()]]
    });
  }

  // Add a new empty step
  public addStep(): void {
    this.steps.push(this.createStepFormGroup());
  }

  // Remove a step at index
  public removeStep(index: number): void {
    const stepControl = this.steps.at(index);
    const stepId = stepControl.get('id')?.value;

    // If step has an ID, it exists in the database and needs to be tracked for deletion
    if (stepId) {
      this.deletedStepIds.push(stepId);
    }

    this.steps.removeAt(index);
  }

  // Load tour data for editing
  public setupTour(): void {
    // Reset form with tour data
    if (this.siteTour) {
      this.form.patchValue({
        name: this.siteTour.name,
        multipage: this.siteTour.multipage || 0,
        pageSlug: this.siteTour.pageSlug || '',
        isActive: this.siteTour.isActive || 0,
        settings: [JSON.stringify(this.siteTour?.settings || {})]
      });

      // Clear steps array
      while (this.steps.length) {
        this.steps.removeAt(0);
      }

      // Add steps if they exist
      if (this.siteTour.steps && this.siteTour.steps.length > 0) {
        this.siteTour.steps.forEach(step => {
          this.steps.push(this.createStepFormGroup(step));
        });
      }
    }
  }

  // Submit the form
  public onSubmit(): void {
    if (this.form.invalid) {
      this.markFormGroupTouched(this.form);
      return;
    }

    this.isSubmitting = true;

    const tourData = { ...this.form.value };
    delete tourData.steps; // Remove steps from tour data

    // Handle JSON conversion for settings
    if (tourData.settings?.length > 0) {
      try {
        // Convert pretty-printed JSON to compact string for storage
        tourData.settings = JSON.stringify(JSON.parse(tourData.settings));
      } catch (e) {
        // This shouldn't happen due to validation, but just in case
        console.error('JSON parsing error:', e);
        return;
      }
    } else {
      // If empty, set to null or default value as needed
      tourData.settings = null;
    }

    if (this.isEditing && this.siteTour?.id) {
      this.updateTour(tourData);
    } else {
      this.createTour(tourData);
    }
  }

  // Create a new tour with steps
  public createTour(tourData: Partial<SiteTour>): void {
    const steps = this.steps.value;
    steps.forEach((step: SiteTourStep) => {
      if (step.id === null) {
        delete step.id;
      }
      // Handle JSON conversion for settings
      if (step.settings?.length > 0) {
        try {
          // Convert pretty-printed JSON to compact string for storage
          step.settings = JSON.stringify(JSON.parse(step.settings));
        } catch (e) {
          // This shouldn't happen due to validation, but just in case
          console.error('JSON parsing error:', e);
          return;
        }
      } else {
        // If empty, set to null or default value as needed
        step.settings = null;
      }
    });

    this.siteTourService.createTourWithSteps(tourData, steps)
      .pipe(finalize(() => this.isSubmitting = false))
      .subscribe({
        next: (tour: SiteTour) => {
          this.dismissDrawer(true);
        },
        error: (error: any) => {
          const errorMessage = typeof error === 'string' ? error : error?.message || ErrorMessages.UNEXPECTED;
          this.bannerNotificationsService.error(`Failed to create tours: ${errorMessage}`);
          this.logger.error('CREATE site tour error', errorMessage);
        }
      });
  }

  // Update an existing tour with steps
  public updateTour(tourData: Partial<SiteTour>): void {
    if (!this.siteTour?.id) return;

    const request: UpdateTourWithStepsRequest = {
      tourData,
      create: [],
      update: [],
      delete: this.deletedStepIds
    };

    // Process steps
    this.steps.controls.forEach(control => {
      const stepData = control.value;
      const stepId = stepData.id;

      // Handle JSON conversion for settings
      if (stepData.settings && stepData.settings.trim() !== '') {
        try {
          // Convert pretty-printed JSON to compact string for storage
          stepData.settings = JSON.stringify(JSON.parse(stepData.settings));
        } catch (e) {
          // This shouldn't happen due to validation, but just in case
          console.error('JSON parsing error:', e);
          return;
        }
      } else {
        // If empty, set to null or default value as needed
        stepData.settings = null;
      }

      // Remove id from the data payload
      const stepToSave = { ...stepData };
      delete stepToSave.id;

      if (stepId) {
        // Existing step - update
        request.update = request.update || [];
        request.update.push({ id: stepId, data: stepToSave });
      } else {
        // New step - create
        request.create = request.create || [];
        request.create.push(stepToSave);
      }
    });

    this.siteTourService.updateTourWithSteps(this.siteTour.id, request)
      .pipe(finalize(() => this.isSubmitting = false))
      .subscribe({
        next: (tour: SiteTour) => {
          this.dismissDrawer(true);
        },
        error: (error: any) => {
          const errorMessage = typeof error === 'string' ? error : error?.message || ErrorMessages.UNEXPECTED;
          this.bannerNotificationsService.error(`Failed to update tour: ${errorMessage}`);
          this.logger.error('UPDATE site tours error', errorMessage);
        }
      });
  }

  public dismissDrawer(result: boolean = false): void {
    this.drawerRef.dismiss(result);
  }

  // Mark all controls in a form group as touched
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        for (const ctrlInArray of control.controls) {
          if (ctrlInArray instanceof FormGroup) {
            this.markFormGroupTouched(ctrlInArray);
          } else {
            ctrlInArray.markAsTouched();
          }
        }
      }
    });
  }
}
